import EventEmitter from "eventemitter3"
import { ItemNode } from "./components/grid/ItemNode"
import { AppData, Log, Router } from "@lightningjs/sdk"
import { AppSetup } from "./app-setup"
import { Helper } from "./helper"
import { FTStreamingPlayer } from "./components/ft-streaming-player"
import { ContentPlayer } from "./components/ft-streaming-player/content-player"
import GridViewModel from "./components/grid/grid-view-model"
import { GRIDTYPE } from "./enum"


export type AutoplayConfig = {
    gridDelay: number
    videoDelay: number
    // prefetchTime: number
    stopOnInteraction: boolean
}

/**
* @class Autoplay
* @description Autoplay class to manage the autoplay functionality of the app.
* It handles the autoplay of videos in grid and mlp.
* It also manages the user interaction and stops the autoplay if required.
* @extends EventEmitter
*/
export class Autoplay extends EventEmitter {
    mlpStack: GridViewModel[] = []
    private gridTimeout: number | null = null
    private videoTimeout: number | null = null
    private handleStartDebounceTimer: number | null = null;

    private config: AutoplayConfig = {
        gridDelay: 5000,
        videoDelay: 15000,
        stopOnInteraction: false,
    }
    contentPlayer!: ContentPlayer
    ftStreamingPlayer!: FTStreamingPlayer
    videoElWrapper!: HTMLElement
    videoEl!: HTMLVideoElement
    isAutoplayStart = false
    playingVodInfo!: ItemNode
    focusVodInfo!: ItemNode
    isAutoPlayGridTimer: boolean = false
    isAutoPlayGridVideoReady: boolean = false

    constructor(_config: AutoplayConfig) {
        super()
        this.config = {
            ...this.config,
            ..._config,
        }
        this.setupGridPlayer()
        window.ftAutoplay = this
    }

    /** 
    * @method setupGridPlayer
    * @description Setup the grid player by initializing the content player and video elements.
    * It sets up the video element and its wrapper for playback.
    * 
    */
    setupGridPlayer() {
        this.contentPlayer = ContentPlayer.getInstance()
        this.contentPlayer._setupVideoPlayer()
        this.videoEl = this.contentPlayer.getVideoEl()
        this.videoElWrapper = this.contentPlayer.getVideoElWrapper()
        this.ftStreamingPlayer = this.contentPlayer.getStreamingPlayer()

    }

    private _getCurrentRowType(): string | null {
        const { appState } = AppData as AppSetup;
        const app = appState?.getApp();
        const currentGrid = app?.navigation.getCurrentGrid();
        
        if (currentGrid && currentGrid.uiList) {
            const focusInfo = currentGrid.getFocusInfo();
            const currentRowIndex = focusInfo.focusRow;
            const currentRow = currentGrid.uiList[currentRowIndex];
            
            if (currentRow && currentRow.data && currentRow.data.item && 
                currentRow.data.item.rowList) {
                return currentRow.data.item.rowList.row_type
            }
        }
        return null
    }

    
    /** 
     * @method start
     * @description Start the autoplay functionality.
     * It sets up the timeouts for grid and video autoplay.
    * It also checks if the item is blocked or adult content and pauses autoplay if required.
    */
    start() {
        this.stop()
        const { appConfig, appState } = AppData as AppSetup;
        const app = appState?.getApp()

        const activeWidget = Router.getActiveWidget()
        if (activeWidget) {
            Log.info('Autoplay disabled because a widget is active', activeWidget);
            return
        }
        
        // Check if current grid is TopCarousel - if so, don't start autoplay
        const currentGrid = app?.navigation.getCurrentGrid()
        if (currentGrid?.type === GRIDTYPE.TOP_CAROUSEL) {
            Log.info('Autoplay disabled for TopCarousel grid');
            return
        }
        
        // Check if current row is non-click - if so, don't start autoplay (only when mid carousel is enabled)
        if (appConfig?.enableMidCarousel && this._getCurrentRowType() === 'non-click') {
            Log.info('Autoplay disabled for non-click row');
            return
        }

        // Check if current row is carousel - if so, don't start autoplay if carousel autoplay is disabled
        if (appConfig?.enableMidCarousel && !appConfig?.enableMidCarouselAutoplay && this._getCurrentRowType() === 'carousel') {
            Log.info('Autoplay disabled for Mid Carousel row');
            return
        }
        
        const vodInfo = app?.navigation.getCurrentVodInfo() as ItemNode
        this.focusVodInfo = vodInfo

        if (!vodInfo) {
            Log.info('No vodInfo found, stopping autoplay');
            return
        }
        if (app?.blockingManager.isItemBlocked(vodInfo) || (Helper.isAdultContent(vodInfo) && !appConfig?.enableAutoplayforAdult) || vodInfo.feed_type == 'live') {
            Log.info('Autoplay paused for adult content or blocked item or live row');
            return
        }
        // if (vodInfo.video_format == "mp4") {
        //     Log.info('Autoplay disabled for mp4 content for now.');
        //     return
        // }

        this.emit("onStart")
        Log.info('Autoplay: , GridTimerStart', performance.now());
        this.getAutoplayItem()

        this.gridTimeout = window.setTimeout(() => {
            this.emit("onGridAutoplay", this.playingVodInfo || this.focusVodInfo)
            Log.info('Autoplay: , GridTimerExecuted', performance.now());

            // Play the preloaded video immediately
            // this.ftStreamingPlayer.play()
            this.isAutoPlayGridTimer = true
            const canPlay = this.checkAndPlayAutoplayGrid()
            if (canPlay) {
                this.ftStreamingPlayer.play()
                this.fadeInVolume(1500); // Trigger fade-in over 1.5 seconds
            }

            this.videoTimeout = window.setTimeout(() => {
                Log.info('Autoplay: , GridToMainPlayerExecuted', performance.now());
                const curTime = Math.round(this.getCurrentTime())
                // this.userInteracted(false)
                this.isAutoplayStart = true
                this.updateMlpStack()

                // Store the current time in local storage or cookie
                if (appConfig?.useLocalStorage == true) {
                    localStorage.setItem(this.playingVodInfo.node_id, curTime);
                } else {
                    Helper.setGeneralCookie(this.playingVodInfo.node_id, curTime, 365);
                }
                this.clearVideoData()
                this.emit("onVideoAutoplay", this.playingVodInfo)
            }, this.config.videoDelay)
        }, this.config.gridDelay - 1000)
    }
    /** 
    * @method userInteracted
    * @description Handle user interaction with the autoplay functionality.
    * It stops the autoplay and clears the video data if required.
    * @param {boolean} stopOnInteraction - Flag to stop autoplay on user interaction.
    */
    userInteracted(stopOnInteraction: boolean = false) {
        Log.info('User interacted with autoplay', stopOnInteraction)
        this.stop()
        this.clearVideoData()
        this.isAutoplayStart = false
        if (stopOnInteraction) {
            this.handleStartDebounced()
        } else {
            this.stop()
        }
    }

    /** 
    * @method checkAndPlayAutoplayGrid
    * @description Check if the autoplay grid video is ready and play it if so.
    */
    checkAndPlayAutoplayGrid() {
        if (this.isAutoPlayGridVideoReady == true && this.isAutoPlayGridTimer == true) {
            Log.info('Autoplay: , checkAndPlayAutoplayGrid', performance.now());

            return true
        }
        return false
    }

    /**
     * @method fadeInVolume
     * @description Fades the volume of the video player from 0 to 1 over the specified duration.
     * @param {number} duration - The duration of the fade-in effect in milliseconds (default 1500).
     */
    fadeInVolume(duration: number = 1500) {
        if (!this.videoEl) return;
        
        this.videoEl.volume = 0;
        const steps = 20;
        const stepTime = duration / steps;
        let currentStep = 0;

        const fadeInterval = setInterval(() => {
            if (!this.videoEl) {
                clearInterval(fadeInterval);
                return;
            }
            
            currentStep++;
            let newVolume = currentStep / steps;
            if (newVolume >= 1) {
                newVolume = 1;
                clearInterval(fadeInterval);
            }
            
            // Catching any potential errors if the video element state changes
            try {
                this.videoEl.volume = newVolume;
            } catch (e) {
                clearInterval(fadeInterval);
            }
        }, stepTime);
    } 
 
    /** 
    * @method stop
    * @description Stop the autoplay functionality.
    * It clears the timeouts for grid and video autoplay.
    * 
    */
    stop() {
        this.isAutoPlayGridTimer = false
        this.isAutoPlayGridVideoReady = false
        if (this.gridTimeout) {
            clearTimeout(this.gridTimeout)
            this.gridTimeout = null
            if (this.isAutoplayStart) {
                this.clearVideoData()
            }
        }
        if (this.videoTimeout) {
            clearTimeout(this.videoTimeout)
            this.videoTimeout = null
            if (this.isAutoplayStart) {
                this.clearVideoData()
            }
        }
    }
    
    /** 
     * @method handleStartDebounced
    * @description Handle the start of the autoplay with a debounce effect.
    * It ensures that the function is not called too frequently.
    * It sets up the timeouts for grid and video autoplay.
    * 
    */
   handleStartDebounced() {
        if (this.handleStartDebounceTimer) {
            clearTimeout(this.handleStartDebounceTimer);
        }
        this.handleStartDebounceTimer = window.setTimeout(() => {
            this.start()
            this.handleStartDebounceTimer = null;
        }, 1000);
    }
    /** 
     * @method preloadVideo
    * @description Preload the video with the provided video data.
    * It sets the video information and handles the resume time for playback.
    * @param {ItemNode} videoData - The video information object.
    * 
    */
    preloadVideo(videoData: ItemNode) {
        if (videoData.video_format === 'mp4') {
            setTimeout(() => {
                this.stop()
            }, 100);
            Log.info('Autoplay disabled for mp4 content.');
            return
        }
        this.clearVideoData()
        this.playingVodInfo = videoData
        const { appConfig } = AppData as AppSetup;
        let resumeTime: any = 0
        if (appConfig?.useLocalStorage == true) {
            resumeTime = localStorage.getItem(videoData.node_id);
        } else {
            resumeTime = Helper.getCookie(videoData.node_id);
        }
        resumeTime = parseInt(resumeTime);
        if (resumeTime == 0 || Number.isNaN(resumeTime)) {
            resumeTime = videoData.intro_et ? (videoData.intro_et + 3) : resumeTime
        }
        this.contentPlayer.setVideoInfo(videoData)
        this.contentPlayer?._resumePlayer(resumeTime, false)
        this.contentPlayer._setEventListeners(this.handleVideoEvent)
        this.setGridPlayerSize()
    }
    
    /** 
     * @method handleVideoEvent
     * @description Handle the video events such as canplay and ended.
     * It checks if the video is ready for immediate playback and handles the end of the video.
     * @param {Event} event - The video event object.
     * 
    */
   handleVideoEvent = (event)=> {
        if (event.type !== 'progress' && event.type !== 'timeupdate') { Log.info('PlayerEvent:', event.type) }
        //Log.info('Video metadata playing, ready for immediate playback.', event.type)
        if (event.type === 'canplay') {
            Log.info('Autoplay: , GridVideoReady', performance.now());
            this.isAutoPlayGridVideoReady = true
            const canPlay = this.checkAndPlayAutoplayGrid()
            Log.info('Autoplay: , GridVideoReady - canPlay', canPlay);

            setTimeout(() => {
                this.ftStreamingPlayer.pause()
                if (canPlay) {
                    this.ftStreamingPlayer.play()
                    this.fadeInVolume(1500);
                }
            }, 100)

            this.contentPlayer._removeEventListener(this.handleVideoEvent)
            // this.ftStreamingPlayer.videoEl.volume = 0
            // this.ftStreamingPlayer.pause()
        }
    }
 

    /** 
    * @method clearVideoData
    * @description Clear the video data by resetting the video player.
    * 
    */
    clearVideoData() {
        if (this.contentPlayer) {
            this.contentPlayer._removeEventListener(this.handleVideoEvent)
            this.contentPlayer.resetPlayer()
        }
    }

    /** 
    * @method getCurrentTime
    * @description Get the current time of the video player.
    * @returns {number} - The current time of the video player.
    * 
    */
    getCurrentTime = () => {
        if (this.videoEl) {
            return this.videoEl.currentTime;
        }
        return 0;
    }

    /** 
    * @method getAutoplayItem
    * @description Get the autoplay item based on the current grid or video.
    * It checks if the item is a list of lists or a video and sets up the content player accordingly.
    * 
    */
    async getAutoplayItem() {
        const { appState, appConfig } = AppData as AppSetup
        const app = appState?.getApp()
        const vodInfo = app?.navigation.getCurrentVodInfo() as ItemNode
        let mlpInfo = null
        this.resetMlpStack()
        if (vodInfo) {
            if (vodInfo.type == 'listoflist' || vodInfo.type == 'new-qid' || vodInfo.feed_type == 'banner') {
                // const mlpGrid = app?.loadShow(vodInfo, vodInfo.id, false)
                // await mlpGrid?.gridModel?.fetchInitialRows()
                // mlpGrid?.createGridListUI()
                try {
                    mlpInfo = await this.loadMlp(vodInfo) as ItemNode
                    if (app?.blockingManager.isItemBlocked(mlpInfo) || 
                        (Helper.isAdultContent(mlpInfo) && !appConfig?.enableAutoplayforAdult)
                     ) {
                        this.stop()
                        this.clearVideoData()
                        this.resetMlpStack()
                        Log.info('Autoplay paused for adult content or blocked item');
                    } else {
                        this.preloadVideo(mlpInfo)
                    }
                    
                } catch (el) {
                    Log.info('getAutoplayItem error', el);
                }
                          
            } else if (vodInfo.feed_type == 'video') {
                //this.contentPlayerSetup(vodInfo)
                if (this.focusVodInfo === vodInfo) {
                    this.preloadVideo(vodInfo)
                }  
            }
        }
    }

    /** 
    * @method setGridPlayerSize
    * @description Set the size of the grid player based on the screen size.
    * It resizes the player to fit the screen and sets the autoplay start flag.
    * 
    */
    setGridPlayerSize() {
        const { appConfig } = AppData as AppSetup;
        const rowType = this._getCurrentRowType()
        const isCarouselOrNonClick = appConfig?.enableMidCarousel && (rowType === 'carousel' || rowType === 'non-click')

        if (isCarouselOrNonClick) {
            const midCarouselWidth = Math.round((800 / 1920) * 100);
            const midCarouselHeight = Math.round((450 / 1080) * 100);
            this.contentPlayer.resizePlayer(9.5, 14.4, midCarouselWidth, midCarouselHeight);
            this.contentPlayer.showCarouselPreview();
        } else {
            const resizePercent = Math.round((1244 / 1920) * 100);
            const xPos = Math.round(((1920 - 1244) / 1920) * 100);
            this.contentPlayer.resizePlayer(xPos, 0, resizePercent, resizePercent);
            this.contentPlayer.resetCarouselPreview();
            if (this.videoEl) {
                this.videoEl.style.opacity = '1';
            }
        }
        
        this.isAutoplayStart = true;
    }

    /** 
    * @method loadMlp
    * @description Load the mlp (multi-level player) with the provided video information.
    * It initializes the mlp grid and checks if the mlp info is a list of lists or a new qid.
    * It recursively calls loadMlp if required.
    * @param {ItemNode} _vodInfo - The video information object.
    * @return {Promise<ItemNode | null>} - The mlp information object or null.
    * 
    */
    async loadMlp(_vodInfo: ItemNode): Promise<ItemNode | null> {

        const { appState } = AppData as AppSetup;
        const app = appState?.getApp()
        const mlpGrid: GridViewModel = (app?.loadShowPrefetch(_vodInfo, _vodInfo.id))!
        await mlpGrid._init()
        mlpGrid.createGridListUI()
        this.mlpStack.push(mlpGrid)
        let mlpInfo = mlpGrid.getFocusedItem()

        //check if mlpInfo is again listoflist or new-qid or banner then recursively call loadMlp
        if (mlpInfo.type == 'listoflist' || mlpInfo.type == 'new-qid' || mlpInfo.feed_type == 'banner') {
            const parentGrid = this.mlpStack[this.mlpStack.length - 1]
            mlpInfo = await this.loadMlp(mlpInfo, parentGrid) as ItemNode
        }

        // console.log('loadMlp mlpInfo', this.mlpStack, mlpInfo);
        return mlpInfo
    }

    /** 
    * @method updateMlpStack
    * @description Update the mlp stack by adding the current mlp grid to the continues stack.
    * It ensures that the mlp stack is not empty and logs the before and after state of the mlp stack.
    * 
    */
    updateMlpStack() {
        const { appState } = AppData as AppSetup;
        const app = appState?.getApp()
        if(this.mlpStack.length == 0) return

        app.navigation.continuesStack = [...app.navigation.continuesStack, ...this.mlpStack]
    }

    /** 
    * @method resetMlpStack
    * @description Reset the mlp stack by destroying all mlp instances in the stack.
    * It clears the mlp stack and destroys the mlp instances to free up resources.
    * 
    */
    resetMlpStack() {
        if(this.mlpStack.length > 0) {
            for(let i = this.mlpStack.length-1; i >= 0; i--) {
                const mlp = this.mlpStack.pop()
                if(mlp) {
                    mlp.destroy()
                }
            }
        }
    }

    /** 
    * @method destroy
    * @description Destroy the autoplay instance and clear the video data.
    * It removes all listeners and resets the video player.
    */
    destroy() {
        this.stop()
        if (this.contentPlayer && this.ftStreamingPlayer) {
            this.contentPlayer.resizePlayer(0, 0, '100', '100')
            this.contentPlayer._removeEventListener(this.handleVideoEvent)
            this.clearVideoData()
            this.videoEl = null
            this.videoElWrapper = null
            this.contentPlayer = null
        }

    }
}